import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import QRCode from "qrcode.react";
import imageCompression from "browser-image-compression";
import { supabase } from "./lib/supabase";
import { useAuth } from "./auth/AuthContext";
import StudioStepTabs, { STUDIO_STEPS } from "./components/StudioStepTabs";
import DataQuotaBar, { QuotaBar } from "./components/DataQuotaBar";
import { AtlasSetupPrompt } from "./components/AtlasPublish";
import {
  ARTIFACT_MAX_BYTES,
  PROJECT_MAX_BYTES,
  formatBytes,
  assertFileWithinLimit,
} from "./lib/limits";
import {
  artifactFilename,
  projectNameOf,
  webArUrl,
} from "./lib/projectPaths";
import {
  atlasFieldsFromProject,
  isAtlasEnabled,
  isAtlasUnplaced,
} from "./lib/atlas";

function nextStudioStep(current) {
  const id = current === "tracker" ? "basics" : current;
  const idx = STUDIO_STEPS.findIndex((s) => s.id === id);
  if (idx < 0 || idx >= STUDIO_STEPS.length - 1) return null;
  return STUDIO_STEPS[idx + 1].id;
}

const API = {
  signArtifactUpload: "/.netlify/functions/signArtifactUpload",
  finalizeProject: "/.netlify/functions/finalizeProject",
  deleteProject: "/.netlify/functions/deleteProject",
  getProjectQuota: "/.netlify/functions/getProjectQuota",
};

const EMPTY_BYTES = { tracker: 0, zip: 0, glb: 0, usdz: 0 };

function sumBytes(map) {
  return Object.values(map).reduce((sum, n) => sum + (Number(n) || 0), 0);
}

const AR_PREFERENCES = ["auto", "native", "webgl"];

function urlForArtifact(project, artifact) {
  if (artifact === "tracker") return project.tracker_url;
  if (artifact === "zip") return project.zip_url;
  if (artifact === "glb") return project.glb_url;
  if (artifact === "usdz") return project.usdz_url;
  return null;
}

export default function Dashboard({ onEditingChange }) {
  const { user, session } = useAuth();
  const [projects, setProjects] = useState([]);
  const [username, setUsername] = useState(null);
  const [maxProjects, setMaxProjects] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editStep, setEditStep] = useState("basics");
  const [form, setForm] = useState({
    title: "",
    description: "",
    ar_preference: "auto",
  });
  const [saving, setSaving] = useState(false);
  const [assetBusy, setAssetBusy] = useState(null);
  const [assetProgress, setAssetProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [bytesByArtifact, setBytesByArtifact] = useState({ ...EMPTY_BYTES });
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const imageInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const glbInputRef = useRef(null);
  const usdzInputRef = useRef(null);
  const quotaReqId = useRef(0);

  const authHeaders = () => ({
    Authorization: `Bearer ${session.access_token}`,
  });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [profileRes, projectsRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("username, max_projects")
          .eq("id", user.id)
          .maybeSingle(),
        supabase
          .from("projects")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false }),
      ]);

      if (profileRes.error) throw profileRes.error;
      if (projectsRes.error) throw projectsRes.error;

      setUsername(profileRes.data?.username || null);
      setMaxProjects(
        profileRes.data?.max_projects != null
          ? Number(profileRes.data.max_projects)
          : null
      );
      setProjects(projectsRes.data || []);
    } catch (err) {
      setError(err.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const editing = projects.find((p) => p.id === editingId) || null;
  const editShareProject = editing
    ? { ...editing, username: username || editing.username }
    : null;
  const editOpenUrl = editShareProject ? webArUrl(editShareProject) : null;
  const qrSize = windowWidth <= 1200 ? 60 : 100;

  useEffect(() => {
    onEditingChange?.(Boolean(editing));
    return () => onEditingChange?.(false);
  }, [editing, onEditingChange]);

  const loadProjectQuota = async (projectId) => {
    const req = ++quotaReqId.current;
    setQuotaLoading(true);
    try {
      const res = await axios.post(
        API.getProjectQuota,
        { projectId },
        { headers: authHeaders() }
      );
      if (req !== quotaReqId.current) return;
      const next = { ...EMPTY_BYTES, ...(res.data?.bytesByArtifact || {}) };
      setBytesByArtifact(next);
    } catch (err) {
      if (req !== quotaReqId.current) return;
      console.error(err);
      setBytesByArtifact({ ...EMPTY_BYTES });
      setError(apiErrorMessage(err) || "Failed to load project data usage");
    } finally {
      if (req === quotaReqId.current) setQuotaLoading(false);
    }
  };

  const openEdit = (project) => {
    setEditingId(project.id);
    setEditStep("basics");
    setForm({
      title: project.title || "",
      description: project.description || "",
      ar_preference: project.ar_preference || "auto",
    });
    setMessage("");
    setError("");
    setAssetBusy(null);
    setAssetProgress(0);
    setBytesByArtifact({ ...EMPTY_BYTES });
    loadProjectQuota(project.id);
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditStep("basics");
    setMessage("");
    setError("");
    setAssetBusy(null);
    setAssetProgress(0);
    setBytesByArtifact({ ...EMPTY_BYTES });
    setQuotaLoading(false);
    quotaReqId.current += 1;
  };

  const apiErrorMessage = (err) => {
    const data = err?.response?.data;
    if (typeof data === "object" && data?.message) return data.message;
    if (typeof data === "object" && data?.error) return data.error;
    if (typeof data === "string") return data;
    return err?.message || "Something went wrong.";
  };

  const saveMeta = async () => {
    if (!editing) return;
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const title = form.title.trim();
      if (!title) throw new Error("Title is required");

      const { error: titleError } = await supabase
        .from("projects")
        .update({ title })
        .eq("id", editing.id);

      if (titleError) throw titleError;

      const finalizePayload = {
        projectId: editing.id,
        description: form.description.trim() || "",
        ar_preference:
          editing.tracking_type === "image" && form.ar_preference === "native"
            ? "auto"
            : form.ar_preference,
        ...atlasFieldsFromProject(editing),
      };

      const finalizeRes = await axios.post(
        API.finalizeProject,
        finalizePayload,
        { headers: authHeaders() }
      );

      const updated = finalizeRes.data;
      setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setForm((prev) => ({
        ...prev,
        title: updated.title || prev.title,
        description: updated.description || "",
        ar_preference: updated.ar_preference || prev.ar_preference,
      }));
      setMessage("Project details saved.");
    } catch (err) {
      setError(apiErrorMessage(err) || err.message || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const setTrackingType = async (type) => {
    if (!editing || type === editing.tracking_type) return;
    if (assetBusy || saving) return;
    setError("");
    setMessage("");
    setSaving(true);
    try {
      const patch = { tracking_type: type };
      if (type === "image" && form.ar_preference === "native") {
        patch.ar_preference = "auto";
      }
      const { data, error: updateError } = await supabase
        .from("projects")
        .update(patch)
        .eq("id", editing.id)
        .select("*")
        .single();
      if (updateError) throw updateError;
      setProjects((prev) =>
        prev.map((p) => (p.id === data.id ? { ...p, ...data } : p))
      );
      if (type === "image" && form.ar_preference === "native") {
        setForm((prev) => ({ ...prev, ar_preference: "auto" }));
      }
      if (type === "world" && editStep === "tracker") {
        setEditStep("basics");
      }
    } catch (err) {
      setError(apiErrorMessage(err) || err.message || "Failed to update tracking");
    } finally {
      setSaving(false);
    }
  };

  const prepareFile = async (artifact, file) => {
    const name = artifactFilename(projectNameOf(editing), artifact);
    if (artifact === "tracker") {
      if (file.type !== "image/jpeg") {
        const compressed = await imageCompression(file, { useWebWorker: true });
        return new File([compressed], name, { type: "image/jpeg" });
      }
      return new File([file], name, { type: "image/jpeg" });
    }
    if (artifact === "zip") {
      return new File([file], name, {
        type: file.type || "application/zip",
      });
    }
    if (artifact === "glb") {
      return new File([file], name, {
        type: file.type || "model/gltf-binary",
      });
    }
    if (artifact === "usdz") {
      return new File([file], name, {
        type: file.type || "model/vnd.usdz+zip",
      });
    }
    return file;
  };

  const swapAsset = async (artifact, rawFile) => {
    if (!editing || !rawFile) return;
    if (
      (artifact === "glb" || artifact === "usdz") &&
      editing.tracking_type !== "world"
    ) {
      setError("GLB and USDZ are only used for world tracking.");
      return;
    }
    setError("");
    setMessage("");

    const previousSize = Number(bytesByArtifact[artifact]) || 0;
    const usedOthers = sumBytes({
      ...bytesByArtifact,
      [artifact]: 0,
    });

    const rawCheck = assertFileWithinLimit(artifact, rawFile.size);
    if (!rawCheck.ok) {
      setError(rawCheck.error);
      return;
    }
    if (usedOthers + rawFile.size > PROJECT_MAX_BYTES) {
      setError(
        `Project data quota exceeded (${formatBytes(usedOthers + rawFile.size)} / ${formatBytes(PROJECT_MAX_BYTES)}).`
      );
      return;
    }

    setAssetBusy(artifact);
    setAssetProgress(0);

    try {
      const file = await prepareFile(artifact, rawFile);
      const check = assertFileWithinLimit(artifact, file.size);
      if (!check.ok) throw new Error(check.error);

      const nextTotal = usedOthers + file.size;
      if (nextTotal > PROJECT_MAX_BYTES) {
        throw new Error(
          `Project data quota exceeded (${formatBytes(nextTotal)} / ${formatBytes(PROJECT_MAX_BYTES)}).`
        );
      }

      // Optimistic: show quota delta as soon as a replacement is chosen
      setBytesByArtifact((prev) => ({ ...prev, [artifact]: file.size }));

      const signRes = await axios.post(
        API.signArtifactUpload,
        {
          projectId: editing.id,
          artifact,
          contentType: file.type || "application/octet-stream",
          fileSize: file.size,
          // Replace semantics: other artifacts only (this object will be overwritten)
          projectBytesUsed: usedOthers,
        },
        { headers: authHeaders() }
      );

      await axios.put(signRes.data.signedUrl, file, {
        headers: { "Content-Type": file.type || "application/octet-stream" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setAssetProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });

      const finalizeRes = await axios.post(
        API.finalizeProject,
        {
          projectId: editing.id,
          ar_preference: form.ar_preference || editing.ar_preference,
          ...atlasFieldsFromProject(editing),
          description:
            form.description.trim() !== ""
              ? form.description.trim()
              : editing.description,
        },
        { headers: authHeaders() }
      );

      const updated = finalizeRes.data;
      setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setBytesByArtifact((prev) => ({ ...prev, [artifact]: file.size }));
      setMessage(
        artifact === "tracker"
          ? "Tracker updated."
          : artifact === "zip"
            ? "ZIP updated."
            : artifact === "glb"
              ? "GLB updated."
              : "USDZ updated."
      );
    } catch (err) {
      console.error(err);
      setBytesByArtifact((prev) => ({ ...prev, [artifact]: previousSize }));
      setError(apiErrorMessage(err));
    } finally {
      setAssetBusy(null);
      setAssetProgress(0);
    }
  };

  const onArtifactFile = async (e, artifact) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await swapAsset(artifact, file);
  };

  const deleteProjectById = async (project) => {
    if (!project) return;
    const ok = window.confirm(
      `Delete “${project.title}”? This removes the project and its uploaded files.`
    );
    if (!ok) return;

    setDeletingId(project.id);
    setError("");
    try {
      await axios.post(
        API.deleteProject,
        { projectId: project.id },
        { headers: authHeaders() }
      );
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      if (editingId === project.id) closeEdit();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  const deleteEditing = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await deleteProjectById(editing);
    } finally {
      setSaving(false);
    }
  };

  const mutating = Boolean(assetBusy) || saving;
  const projectBytesUsed = sumBytes(bytesByArtifact);

  if (editing) {
    const trackerUrl = urlForArtifact(editing, "tracker");
    const zipUrl = urlForArtifact(editing, "zip");
    const glbUrl = urlForArtifact(editing, "glb");
    const usdzUrl = urlForArtifact(editing, "usdz");
    const trackerBytes = Number(bytesByArtifact.tracker) || 0;
    const zipBytes = Number(bytesByArtifact.zip) || 0;
    const glbBytes = Number(bytesByArtifact.glb) || 0;
    const usdzBytes = Number(bytesByArtifact.usdz) || 0;

    return (
      <div className="studio-layout studio-layout--solo">
        <div className="studio-panel">
          <div className="studio-panel__face">
          <StudioStepTabs
            step={editStep}
            onSelect={(id) => {
              if (mutating) return;
              setError("");
              setMessage("");
              setEditStep(id === "tracker" ? "basics" : id);
            }}
          />
          <div className="form-container">
            {error && <div className="studio-error">{error}</div>}
            {message && !error && (
              <div className="studio-success">{message}</div>
            )}
            <DataQuotaBar usedBytes={projectBytesUsed} loading={quotaLoading} />

            <div className="studio-form-body">
              <div
                key={editStep === "tracker" ? "basics" : editStep}
                className="studio-step-pane"
              >
              {(editStep === "basics" || editStep === "tracker") && (
                <div className="setup-step">
                  <label>
                    <div className="title-container">Project title</div>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, title: e.target.value }))
                      }
                      placeholder="My AR project"
                      disabled={mutating}
                    />
                  </label>

                  <div className="title-container">Tracking type</div>
                  <div
                    className="seg-control"
                    role="group"
                    aria-label="Tracking type"
                  >
                    <button
                      type="button"
                      className={`seg-control__btn ${
                        editing.tracking_type === "image"
                          ? "seg-control__btn--active"
                          : ""
                      }`}
                      disabled={mutating}
                      onClick={() => setTrackingType("image")}
                    >
                      Image
                    </button>
                    <button
                      type="button"
                      className={`seg-control__btn ${
                        editing.tracking_type === "world"
                          ? "seg-control__btn--active"
                          : ""
                      }`}
                      disabled={mutating}
                      onClick={() => setTrackingType("world")}
                    >
                      World
                    </button>
                  </div>

                  {editing.tracking_type === "world" ? (
                    <AtlasSetupPrompt onAtlas={isAtlasEnabled(editing)} />
                  ) : (
                    <div
                      className={`drop-zone drop-zone--compact ${
                        assetBusy === "tracker" ? "drop-zone--busy" : ""
                      } ${trackerUrl ? "drop-zone--has-preview" : ""}`}
                      onClick={() => {
                        if (mutating) return;
                        imageInputRef.current?.click();
                      }}
                    >
                      {assetBusy === "tracker" ? (
                        <>Uploading… {assetProgress}%</>
                      ) : trackerUrl ? (
                        <>
                          <img
                            src={trackerUrl}
                            alt="Tracker"
                            className="tracker-preview"
                          />
                          <span className="size-hint">
                            Replace
                            {trackerBytes > 0
                              ? ` · ${formatBytes(trackerBytes)}`
                              : ""}
                          </span>
                        </>
                      ) : (
                        <>
                          Select your tracker image
                          <br />
                          <span className="size-hint">
                            Max {formatBytes(ARTIFACT_MAX_BYTES.tracker)}
                          </span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        ref={imageInputRef}
                        onChange={(e) => onArtifactFile(e, "tracker")}
                        disabled={mutating}
                        style={{ display: "none" }}
                      />
                    </div>
                  )}
                </div>
              )}

              {editStep === "files" && (
                <div className="files-step">
                  <div className="native-asset-row">
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={mutating}
                      onClick={() => zipInputRef.current?.click()}
                    >
                      {assetBusy === "zip"
                        ? `Uploading… ${assetProgress}%`
                        : zipBytes > 0
                          ? `ZIP ✓ (${formatBytes(zipBytes)})`
                          : zipUrl
                            ? "Replace ZIP"
                            : "Add ZIP"}
                    </button>
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={mutating || editing.tracking_type !== "world"}
                      onClick={() => glbInputRef.current?.click()}
                      title={
                        editing.tracking_type !== "world"
                          ? "GLB is only used for world tracking (Scene Viewer)"
                          : undefined
                      }
                    >
                      {editing.tracking_type !== "world"
                        ? "GLB (world only)"
                        : assetBusy === "glb"
                          ? `Uploading… ${assetProgress}%`
                          : glbBytes > 0
                            ? `GLB ✓ (${formatBytes(glbBytes)})`
                            : glbUrl
                              ? "Replace GLB"
                              : "Add GLB"}
                    </button>
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={mutating || editing.tracking_type !== "world"}
                      onClick={() => usdzInputRef.current?.click()}
                      title={
                        editing.tracking_type !== "world"
                          ? "USDZ is only used for world tracking (Quick Look)"
                          : undefined
                      }
                    >
                      {editing.tracking_type !== "world"
                        ? "USDZ (world only)"
                        : assetBusy === "usdz"
                          ? `Uploading… ${assetProgress}%`
                          : usdzBytes > 0
                            ? `USDZ ✓ (${formatBytes(usdzBytes)})`
                            : usdzUrl
                              ? "Replace USDZ"
                              : "Add USDZ"}
                    </button>
                  </div>
                  <p
                    className="size-hint"
                    style={{ textAlign: "center", margin: 0 }}
                  >
                    ZIP max {formatBytes(ARTIFACT_MAX_BYTES.zip)}
                    {editing.tracking_type !== "world"
                      ? " · Quick Look / Scene Viewer require world tracking"
                      : ""}
                  </p>
                  {glbUrl && (
                    <div className="model-viewer-wrap">
                      <model-viewer
                        src={glbUrl}
                        ar
                        ar-modes="webxr scene-viewer quick-look"
                        camera-controls
                        style={{ width: "100%", height: "220px" }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".zip,application/zip"
                    ref={zipInputRef}
                    onChange={(e) => onArtifactFile(e, "zip")}
                    disabled={mutating}
                    style={{ display: "none" }}
                  />
                  <input
                    type="file"
                    accept=".glb,model/gltf-binary"
                    ref={glbInputRef}
                    onChange={(e) => onArtifactFile(e, "glb")}
                    disabled={editing.tracking_type !== "world"}
                    style={{ display: "none" }}
                  />
                  <input
                    type="file"
                    accept=".usdz,model/vnd.usdz+zip"
                    ref={usdzInputRef}
                    onChange={(e) => onArtifactFile(e, "usdz")}
                    disabled={editing.tracking_type !== "world"}
                    style={{ display: "none" }}
                  />
                </div>
              )}

              {editStep === "preference" && (
                <div className="ar-preference-selection">
                  <div
                    className="title-container"
                    style={{ textAlign: "center" }}
                  >
                    AR preference
                  </div>
                  {AR_PREFERENCES.map((pref) => {
                    const nativeBlocked =
                      pref === "native" && editing.tracking_type !== "world";
                    return (
                      <button
                        key={pref}
                        type="button"
                        className={
                          form.ar_preference === pref
                            ? "button button-primary"
                            : "button button-secondary"
                        }
                        disabled={mutating || nativeBlocked}
                        onClick={() =>
                          setForm((prev) => ({ ...prev, ar_preference: pref }))
                        }
                        title={
                          nativeBlocked
                            ? "Native AR is only available for world tracking"
                            : undefined
                        }
                      >
                        {pref === "auto"
                          ? "Auto"
                          : pref === "native"
                            ? "Native"
                            : "WebGL"}
                      </button>
                    );
                  })}
                  <p className="deploy-blurb">
                    <strong>Native</strong> — device AR (Quick Look / Scene
                    Viewer) for world tracking. <strong>WebGL</strong>{" "}
                    (ArgonWebgl) — browser-based AR via Argon’s WebGL viewers.{" "}
                    <strong>Auto</strong> — prefer Native when available,
                    otherwise WebGL.
                  </p>
                  {editing.tracking_type !== "world" && (
                    <p
                      className="size-hint"
                      style={{ textAlign: "center", margin: 0 }}
                    >
                      Image tracking uses WebGL only (no Quick Look / Scene
                      Viewer)
                    </p>
                  )}
                </div>
              )}

              {editStep === "publish" && (
                <div className="publish-step">
                  {editing.tracking_type === "image" && trackerUrl && (
                    <img
                      src={trackerUrl}
                      alt="Tracker"
                      className="tracker-preview tracker-preview--publish"
                    />
                  )}
                  {editOpenUrl ? (
                    <a
                      href={editOpenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="publish-qr-link"
                    >
                      <div className="qr-code">
                        <QRCode
                          value={editOpenUrl}
                          size={qrSize}
                          bgColor="transparent"
                          fgColor="#0a0a0a"
                        />
                      </div>
                      <span className="publish-hint">
                        Scan to open the live experience
                      </span>
                    </a>
                  ) : (
                    <p className="publish-hint">
                      QR unavailable until a username is set.
                    </p>
                  )}
                  <button
                    type="button"
                    className="button button-primary"
                    disabled={mutating}
                    onClick={saveMeta}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    className="button button-secondary"
                    disabled={mutating}
                    onClick={deleteEditing}
                  >
                    Delete project
                  </button>
                </div>
              )}
              </div>
            </div>

            <button
              type="button"
              className="button button-secondary studio-back-btn"
              disabled={mutating}
              onClick={() => {
                if (editStep === "publish") {
                  closeEdit();
                  return;
                }
                const next = nextStudioStep(editStep);
                if (!next) return;
                setError("");
                setMessage("");
                setEditStep(next);
              }}
            >
              {editStep === "publish" ? "Go to Dashboard" : "Next"}
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="studio-layout studio-layout--solo">
      <div className="studio-panel studio-panel--plain">
        <div className="studio-panel__face">
          <div className="form-container">
            <div className="panel-heading">
              <h1>Dashboard</h1>
            </div>

            {maxProjects != null && (
              <div className="dashboard-quota">
                <QuotaBar
                  label="Total projects"
                  used={projects.length}
                  max={maxProjects}
                  ariaLabel="Total projects"
                />
              </div>
            )}

            {error && <div className="studio-error">{error}</div>}

            <div className="studio-form-body">
              {loading ? (
                <div className="panel-muted">Loading projects…</div>
              ) : projects.length === 0 ? (
                <div className="panel-muted">No projects yet</div>
              ) : (
                <ul className="project-list">
                  {projects.map((p) => {
                    const handle = username || p.username;
                    const withUser = { ...p, username: handle };
                    const openUrl = webArUrl(withUser);
                    return (
                      <li key={p.id} className="project-row">
                        <div className="project-row__main">
                          <div className="project-row__title">{p.title}</div>
                          <div className="project-row__meta">
                            <span
                              className="project-chip"
                              title={
                                p.tracking_type === "world"
                                  ? "World tracking"
                                  : "Image tracking"
                              }
                            >
                              {p.tracking_type === "world" ? "WT" : "IT"}
                            </span>
                            <span
                              className="project-chip"
                              title="AR preference"
                            >
                              {p.ar_preference === "native"
                                ? "Native"
                                : p.ar_preference === "webgl"
                                  ? "WebGL"
                                  : "Auto"}
                            </span>
                            {isAtlasEnabled(p) && (
                              <span className="project-chip project-chip--atlas">
                                {isAtlasUnplaced(p)
                                  ? "Atlas · Unplaced"
                                  : "Atlas"}
                              </span>
                            )}
                          </div>
                          {openUrl && (
                            <a
                              className="project-row__path"
                              href={openUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={openUrl}
                            >
                              {openUrl}
                            </a>
                          )}
                        </div>
                        <div className="project-row__actions">
                          <button
                            type="button"
                            className="button button-secondary project-row__btn"
                            onClick={() => openEdit(p)}
                            disabled={Boolean(deletingId)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="button button-secondary project-row__btn"
                            onClick={() => deleteProjectById(p)}
                            disabled={deletingId === p.id}
                          >
                            {deletingId === p.id ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
